import React, { useState } from "react";
import {
  Modal,
  Button,
  Group,
  Stack,
  SegmentedControl,
  Text,
} from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { saveAs } from "file-saver";
import { pdf } from "@react-pdf/renderer";
import { exportEvvRecords } from "../../services/UserTracking";
import { decrypt } from "../../utils";
import { EvvRecordsPdf } from "./EvvRecordsPdf";

const getStaffName = (staff) => {
  if (!staff) return "N/A";
  return (
    staff?.name ||
    `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() ||
    "N/A"
  );
};

const getClientName = (client) => {
  if (!client) return "N/A";
  const f1 = client?.form1;
  if (f1?.firstName || f1?.lastName) {
    return `${f1.firstName || ""} ${f1.lastName || ""}`.trim();
  }
  if (f1?.name) return f1.name;
  if (client?.name) {
    try {
      return decrypt(client.name, false) || client.name;
    } catch {
      return client.name;
    }
  }
  return "N/A";
};

const fmtTs = (ts) =>
  ts ? new Date(ts).toISOString().replace("T", " ").slice(0, 19) : "";

const fmtDist = (d) => (d != null ? `${Number(d).toFixed(2)} mi` : "");

const buildExportRecords = (allRecords) =>
  allRecords.map((r) => {
    const ci = r.evvClockIn;
    const co = r.evvClockOut;
    return {
      staffName: getStaffName(r?.staff),
      date: r.date?.date ? new Date(r.date.date).toISOString().split("T")[0] : "",
      facility: r.facility?.name || "",
      type: r.checkin_type || "",
      clientName: getClientName(r.client),
      service: r.service || "",
      evvEnforced: r.evvEnforced ? "Yes" : "No",
      status: r.endTime ? "Completed" : "Still Clocked In",
      clockInTime: fmtTs(ci?.timestamp),
      clockInLat: ci?.location?.lat ?? "",
      clockInLng: ci?.location?.lng ?? "",
      clockInAccuracy: ci?.location?.accuracy ?? "",
      clockInDistance: fmtDist(ci?.distanceToSite),
      clockInAddress: ci?.matchedAddress || "",
      clockInDeviceType: ci?.deviceInfo?.deviceType || "",
      clockInLocationSource: ci?.deviceInfo?.locationSource || "",
      clockInIp: ci?.deviceInfo?.ip || "",
      clockOutTime: fmtTs(co?.timestamp),
      clockOutLat: co?.location?.lat ?? "",
      clockOutLng: co?.location?.lng ?? "",
      clockOutAccuracy: co?.location?.accuracy ?? "",
      clockOutDistance: fmtDist(co?.distanceToSite),
      clockOutAddress: co?.matchedAddress || "",
      clockOutDeviceType: co?.deviceInfo?.deviceType || "",
      clockOutLocationSource: co?.deviceInfo?.locationSource || "",
      clockOutIp: co?.deviceInfo?.ip || "",
      serviceAddress: ci?.serviceAddress || co?.serviceAddress || "",
    };
  });

const EvvDownloadFilterModal = ({ opened, onClose, buildQuery, facilityName: filterFacilityName }) => {
  const [format, setFormat] = useState("csv");
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const query = buildQuery();
      const allRecords = await exportEvvRecords(query);
      if (!allRecords.length) return;

      const exportRows = buildExportRecords(allRecords);

      // Determine facility name: use filter selection, or if all records share the same facility, use that
      let resolvedFacilityName = filterFacilityName;
      if (!resolvedFacilityName && exportRows.length > 0) {
        const uniqueFacilities = [...new Set(exportRows.map((r) => r.facility).filter(Boolean))];
        if (uniqueFacilities.length === 1) resolvedFacilityName = uniqueFacilities[0];
      }

      const fileBase = resolvedFacilityName
        ? `${resolvedFacilityName.replace(/[^a-zA-Z0-9 ]/g, "")}_EVV_Records_${query.dateFrom || "all"}_${query.dateTo || "all"}`
        : `EVV_Records_${query.dateFrom || "all"}_${query.dateTo || "all"}`;

      if (format === "pdf") {
        const doc = (
          <EvvRecordsPdf
            data={exportRows}
            dateRange={{ start: query.dateFrom, end: query.dateTo }}
            facilityName={resolvedFacilityName}
          />
        );
        const asPdf = pdf();
        asPdf.updateContainer(doc);
        const blob = await asPdf.toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileBase}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        onClose();
        return;
      }

      if (format === "json") {
        const blob = new Blob([JSON.stringify(exportRows, null, 2)], {
          type: "application/json;charset=utf-8",
        });
        saveAs(blob, `${fileBase}.json`);
        onClose();
        return;
      }

      if (format === "xml") {
        const escXml = (val) =>
          String(val ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
        let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<EvvRecords>\n';
        exportRows.forEach((r) => {
          xml += "  <Record>\n";
          Object.entries(r).forEach(([key, val]) => {
            xml += `    <${key}>${escXml(val)}</${key}>\n`;
          });
          xml += "  </Record>\n";
        });
        xml += "</EvvRecords>";
        const blob = new Blob([xml], {
          type: "application/xml;charset=utf-8",
        });
        saveAs(blob, `${fileBase}.xml`);
        onClose();
        return;
      }

      // CSV default
      const headers = [
        "Staff Name", "Date", "Facility", "Type", "Client Name", "Service",
        "EVV Enforced", "Status",
        "Clock-In Time", "Clock-In Lat", "Clock-In Lng", "Clock-In Accuracy",
        "Clock-In Distance", "Clock-In Address", "Clock-In Device", "Clock-In Location Source", "Clock-In IP",
        "Clock-Out Time", "Clock-Out Lat", "Clock-Out Lng", "Clock-Out Accuracy",
        "Clock-Out Distance", "Clock-Out Address", "Clock-Out Device", "Clock-Out Location Source", "Clock-Out IP",
        "Service Address",
      ];
      const escCsv = (val) => {
        const s = String(val ?? "");
        return s.includes(",") || s.includes('"') || s.includes("\n")
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };
      const rows = [headers.join(",")];
      exportRows.forEach((r) => {
        rows.push(Object.values(r).map(escCsv).join(","));
      });
      const blob = new Blob([rows.join("\n")], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(blob, `${fileBase}.csv`);
      onClose();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Download EVV Records</Text>}
      size="sm"
      centered
      overlayProps={{ blur: 4 }}
      radius="lg"
      zIndex={1000}
      
    >
      <Stack spacing="md">
        <Text fz={13} color="dimmed">
          The download will use the filters currently applied to the table.
        </Text>
        <div>
          <Text fz={14} fw={500} mb={4}>
            Format
          </Text>
          <SegmentedControl
            fullWidth
            value={format}
            onChange={setFormat}
            data={[
              { label: "CSV", value: "csv" },
              { label: "JSON", value: "json" },
              { label: "XML", value: "xml" },
              { label: "PDF", value: "pdf" },
            ]}
          />
        </div>
        <Group position="right" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="yellow"
            c="#000"
            leftIcon={<IconDownload size={14} />}
            onClick={handleDownload}
            loading={downloading}
          >
            Download
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EvvDownloadFilterModal;
